import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import CategoryPage from './pages/CategoryPage';
import CertificatePage from './pages/CertificatePage';
import SearchPage from './pages/SearchPage';
import { PPEData } from './types/ppe';
import ppeDataJson from './data/ppeCertificates.json';
import './App.css';

// Custom hook to scroll to top on route changes
function useScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
}

// Component to handle title updates and meta tags based on route
function TitleUpdater({ data }: { data: PPEData | null }) {
  const location = useLocation();
  
  useEffect(() => {
    if (!data) {
      document.title = 'PPE Certificate Portal - Loading...';
      updateMetaDescription('Loading PPE Certificate Portal...');
      return;
    }
    
    const path = location.pathname;
    let title = 'PPE Certificate Portal';
    let description = 'Find and verify personal protective equipment certificates. Browse PPE standards, categories, and certification requirements.';
    
    if (path === '/') {
      title = 'PPE Certificate Portal - Personal Protective Equipment Certificates';
      description = 'Comprehensive database of PPE certificates and standards. Find safety equipment certifications, risk levels, and compliance requirements for personal protective equipment.';
    } else if (path === '/categories') {
      title = 'PPE Certificate Categories - Browse All Product Types';
      description = 'Browse all PPE certificate categories including head protection, eye protection, respiratory protection, and more. Find the right safety equipment for your needs.';
    } else if (path.startsWith('/category/')) {
      const categoryId = path.split('/')[2];
      const category = data.categories.find(cat => cat.id === categoryId);
      if (category) {
        title = `${category.name} PPE Certificates - ${category.description}`;
        description = `Browse ${category.name.toLowerCase()} PPE certificates and standards. ${category.description} Find certification requirements and compliance information.`;
      } else {
        title = 'PPE Certificate Category - Personal Protective Equipment';
        description = 'PPE certificate category information. Find safety equipment standards and certification requirements.';
      }
    } else if (path.startsWith('/certificate/')) {
      const certificateId = path.split('/')[2];
      let certificate = null;
      let category = null;
      
      // Find the certificate and its category
      for (const cat of data.categories) {
        const found = cat.certificates.find(cert => cert.id === certificateId);
        if (found) {
          certificate = found;
          category = cat;
          break;
        }
      }
      
      if (certificate && category) {
        title = `${certificate.code} - ${certificate.name} | ${category.name} PPE Certificate`;
        description = `${certificate.code} PPE certificate: ${certificate.name}. ${certificate.description} Risk level ${certificate.riskLevel}. Find compliance requirements and product examples.`;
      } else {
        title = 'PPE Certificate Details - Personal Protective Equipment';
        description = 'Detailed PPE certificate information including standards, compliance requirements, and safety specifications.';
      }
    } else if (path === '/search') {
      title = 'Search PPE Certificates - Find Personal Protective Equipment Standards';
      description = 'Search our comprehensive database of PPE certificates. Find safety equipment standards, certification codes, and compliance requirements by keyword or category.';
    }
    
    document.title = title;
    updateMetaDescription(description);
    updateSocialMetaTags(title, description);
    addStructuredData(data, path);
  }, [location, data]);
  
  return null;
}

// Helper function to update meta description
function updateMetaDescription(description: string) {
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);
}

// Helper function to update Open Graph and Twitter meta tags
function updateSocialMetaTags(title: string, description: string) {
  // Update Open Graph tags
  updateMetaTag('property', 'og:title', title);
  updateMetaTag('property', 'og:description', description);
  
  // Update Twitter tags
  updateMetaTag('name', 'twitter:title', title);
  updateMetaTag('name', 'twitter:description', description);
}

// Helper function to update or create meta tags
function updateMetaTag(attribute: string, value: string, content: string) {
  let metaTag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('content', content);
    metaTag.setAttribute(attribute, value);
    document.head.appendChild(metaTag);
  } else {
    metaTag.setAttribute('content', content);
  }
}

// Helper function to add structured data for SEO
function addStructuredData(data: PPEData, path: string) {
  // Remove existing structured data
  const existingStructuredData = document.querySelectorAll('script[type="application/ld+json"]');
  existingStructuredData.forEach(el => el.remove());
  
  if (path === '/') {
    // Organization schema for homepage
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "PPE Certificate Portal",
      "description": "Comprehensive database of PPE certificates and standards",
      "url": window.location.origin,
      "logo": `${window.location.origin}/ppecp-logo.png`,
      "sameAs": []
    };
    
    addStructuredDataScript(organizationSchema);
  } else if (path.startsWith('/category/')) {
    const categoryId = path.split('/')[2];
    const category = data.categories.find(cat => cat.id === categoryId);
    
    if (category) {
      const categorySchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${category.name} PPE Certificates`,
        "description": category.description,
        "url": window.location.href,
        "mainEntity": {
          "@type": "ItemList",
          "numberOfItems": category.certificates.length,
          "itemListElement": category.certificates.map((cert, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "CreativeWork",
              "name": cert.name,
              "description": cert.description,
              "identifier": cert.code
            }
          }))
        }
      };
      
      addStructuredDataScript(categorySchema);
    }
  } else if (path.startsWith('/certificate/')) {
    const certificateId = path.split('/')[2];
    let certificate = null;
    let category = null;
    
    for (const cat of data.categories) {
      const found = cat.certificates.find(cert => cert.id === certificateId);
      if (found) {
        certificate = found;
        category = cat;
        break;
      }
    }
    
    if (certificate && category) {
      const certificateSchema = {
        "@context": "https://schema.org",
        "@type": "CreativeWork",
        "name": `${certificate.code} - ${certificate.name}`,
        "description": certificate.description,
        "identifier": certificate.code,
        "category": category.name,
        "url": window.location.href,
        "author": {
          "@type": "Organization",
          "name": "PPE Certificate Portal"
        }
      };
      
      addStructuredDataScript(certificateSchema);
    }
  }
}

// Helper function to add structured data script to head
function addStructuredDataScript(data: any) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function App() {
  const [ppeData, setPpeData] = useState<PPEData | null>(null);

  useEffect(() => {
    setPpeData(ppeDataJson as PPEData);
  }, []);

  if (!ppeData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-brand-yellow rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-black mb-2">Loading PPE Certificate Portal</h2>
          <p className="text-gray-600">Please wait while we load the certificate database...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <TitleUpdater data={ppeData} />
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Header data={ppeData} />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage data={ppeData} />} />
            <Route path="/categories" element={<CategoriesPage data={ppeData} />} />
            <Route path="/category/:categoryId" element={<CategoryPage data={ppeData} />} />
            <Route path="/certificate/:certificateId" element={<CertificatePage data={ppeData} />} />
            <Route path="/search" element={<SearchPage data={ppeData} />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

// Component that uses the scroll to top hook
function ScrollToTop() {
  useScrollToTop();
  return null;
}

export default App;
